<script setup lang="ts">
import { computed } from 'vue'

import ContentHeading from '@fondof-ui/components/content/ContentHeading.vue'
import ContentHeadingRoot from '@fondof-ui/components/content/ContentHeadingRoot.vue'
import ContentRoot from '@fondof-ui/components/content/ContentRoot.vue'
import type { HTMLAttributeClass } from '@fondof-ui/types'
import type { BrandHero } from '@storefront-satch/components/molecules/hero/types'

const props = defineProps<{
  /**
   * Changes the color of the Hero Content
   */
  contentStyle: BrandHero['contentStyle']
  /**
   * The Content for the Hero component. It contains the Headline, HeadlineLevel, subline, the text and also the content alignment.
   */
  content: BrandHero['content']
  /**
   * Controls the font size of the hero content headline
   */
  headlineSize: BrandHero['headlineSize']
  /**
   * Controls the style of the content headline
   */
  headlineStyle?: BrandHero['headlineStyle']
  /**
   * allows to set classes specific to the headline
   */
  classHeadline?: HTMLAttributeClass['class']
  /**
   * allows to set classes specific to the subline
   */
  classSubline?: HTMLAttributeClass['class']
  /**
   * boolean for wether the hero as an additonal Media like an image or video, or not
   */
  hasAdditionalImage: boolean
}>()

const textColorClasses = computed(() => ({
  'text-white': props.contentStyle === 'white',
  'text-black': props.contentStyle === 'black',
}))
</script>
<template>
  <ContentRoot
    v-if="props.content"
    :alignment="props.content.alignment"
    class="hyphens-auto"
    style="hyphenate-limit-chars: 10 3 4"
  >
    <ContentHeadingRoot
      v-slot="{ headlineElement, sublineElement }"
      data-label="headline wrapper"
      :headline-level="props.content.headlineLevel ?? null"
      class="flex flex-wrap"
    >
      <ContentHeading
        :is="headlineElement"
        v-if="props.content.headline"
        :class="[
          'w-full break-words font-bold !leading-[1.1]',
          {
            'font-sans-condensed uppercase': props.headlineStyle === 'sat free',
            'font-sans-bold': props.headlineStyle !== 'sat free',
          },
          props.classHeadline,
          textColorClasses,
        ]"
      >
        {{ props.content.headline }}
      </ContentHeading>
      <ContentHeading
        :is="sublineElement"
        v-if="props.content.subline ?? undefined"
        :class="[
          'w-full md:w-3/5',
          {
            'font-sans-condensed': props.headlineStyle === 'sat free',
            'text-lg': !hasAdditionalImage,
            'text-md': hasAdditionalImage,
            'mt-3': props.content.headline,
          },
          props.classSubline,
          textColorClasses,
        ]"
      >
        {{ props.content.subline }}
      </ContentHeading>
    </ContentHeadingRoot>
  </ContentRoot>
</template>
